import { color } from '@/theme/tokens';

// Helper to calculate color for simple alternation (3+ columns)
export const getSimpleAlternationColor = (index: number): string => {
    return index % 2 === 0 ? color.gray[50] : color.gray[40];
};

// Helper to calculate color for checkerboard pattern (2 columns)
// Pattern: 50, 40 | 40, 50 | 50, 40 | 40, 50 ...
export const getCheckerboardColor = (index: number, columnsPerRow: number): string => {
    const row = Math.floor(index / columnsPerRow);
    const position = index % columnsPerRow;
    return (row + position) % 2 === 0 ? color.gray[50] : color.gray[40];
};

/**
 * Determines if the last row should be centered based on column count and items in last row.
 *
 * Centering rules:
 * - 3 columns: center if 1 item in last row
 * - 5 columns: center if 3 items in last row
 *
 * Formula: center if itemsInLastRow === columnsPerRow - 2
 */
export const shouldCenterLastRow = (itemsInLastRow: number, columnsPerRow: number): boolean => {
    if (columnsPerRow < 3) return false;
    return itemsInLastRow === columnsPerRow - 2;
};

/**
 * Helper to calculate color based on VISUAL grid position (for 3+ columns).
 * This accounts for centered items in the last row.
 *
 * When items are centered in the last row, their visual column positions
 * are offset to maintain the checkerboard pattern correctly.
 */
export const getCheckerboardColorWithCentering = (
    index: number,
    totalItems: number,
    columnsPerRow: number
): string => {
    const row = Math.floor(index / columnsPerRow);
    const itemsInLastRow = totalItems % columnsPerRow || columnsPerRow;
    const isInLastRow = index >= totalItems - itemsInLastRow;

    let visualColumn: number;

    if (isInLastRow && shouldCenterLastRow(itemsInLastRow, columnsPerRow)) {
        // Calculate the starting column for centered items
        // For 3 cols with 1 item: start at column 1 (middle)
        // For 5 cols with 3 items: start at column 1 (positions 1,2,3)
        const startColumn = Math.floor((columnsPerRow - itemsInLastRow) / 2);
        const positionInLastRow = index - (totalItems - itemsInLastRow);
        visualColumn = startColumn + positionInLastRow;
    } else {
        // Normal position
        visualColumn = index % columnsPerRow;
    }

    return (row + visualColumn) % 2 === 0 ? color.gray[50] : color.gray[40];
};


/**
 * Helper to get grid-column for centering items in last row.
 *
 * Returns the 1-indexed column number for CSS grid-column property.
 * Returns 'auto' for items that don't need explicit positioning.
 */
export const getGridColumn = (index: number, totalItems: number, columnsPerRow: number): number | 'auto' => {
    const itemsInLastRow = totalItems % columnsPerRow || columnsPerRow;
    const isInLastRow = index >= totalItems - itemsInLastRow;

    if (isInLastRow && shouldCenterLastRow(itemsInLastRow, columnsPerRow)) {
        // Calculate the starting column (1-indexed for CSS grid)
        // For 3 cols with 1 item: start at column 2
        // For 5 cols with 3 items: start at column 2
        const startColumn = Math.floor((columnsPerRow - itemsInLastRow) / 2) + 1;
        const positionInLastRow = index - (totalItems - itemsInLastRow);
        return startColumn + positionInLastRow;
    }

    return 'auto';
};

// Helper to determine if the current item is the last in its row.
export const isLastInRow = (index: number, itemsPerRow: number): boolean => {
    return (index + 1) % itemsPerRow === 0;
};
